import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { 
  BellIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  SwatchIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import WarrantyPeriodSettings from '../components/WarrantyPeriodSettings';

const AdminNotificationSettings = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('targets');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testEmailSubject, setTestEmailSubject] = useState('테스트 이메일');
  const [testEmailContent, setTestEmailContent] = useState('건축시공 플랫폼 테스트 이메일입니다.');
  const [testEmailIsHtml, setTestEmailIsHtml] = useState(false);
  const [testMessage, setTestMessage] = useState('건축시공 플랫폼 테스트 메시지입니다.');
  const [expandedNotifications, setExpandedNotifications] = useState({});
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('simple');
  const [emailTemplateSettings, setEmailTemplateSettings] = useState({});
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // 알림 대상 조회
  const { data: notificationTargets, refetch: refetchTargets } = useQuery(
    'notification-targets',
    () => api.get('/notifications/targets').then(res => res.data),
    {
      onError: (error) => {
        console.error('알림 대상 조회 실패:', error);
        toast.error('알림 대상을 불러오는데 실패했습니다');
      },
      onSuccess: (data) => {
        // 모든 알림 유형을 기본적으로 열어둠
        const allExpanded = {};
        Object.keys(data || {}).forEach(key => {
          allExpanded[key] = true;
        });
        setExpandedNotifications(allExpanded);
      }
    }
  );

  // 알림 템플릿 조회
  const { data: templates, refetch: refetchTemplates } = useQuery(
    'notification-templates',
    () => api.get('/notifications/templates').then(res => res.data),
    {
      enabled: activeTab === 'templates',
      onError: (error) => {
        console.error('템플릿 조회 실패:', error);
        toast.error('템플릿을 불러오는데 실패했습니다');
      }
    }
  );

  // API 설정 조회
  const { data: apiSettings, refetch: refetchApiSettings } = useQuery(
    'api-settings',
    () => api.get('/notifications/api-settings').then(res => res.data),
    {
      onError: (error) => {
        console.error('API 설정 조회 실패:', error);
        toast.error('API 설정을 불러오는데 실패했습니다');
      }
    }
  );

  // SMS 잔액 조회
  const { data: smsBalance } = useQuery(
    'sms-balance',
    () => api.get('/notifications/sms/balance').then(res => res.data),
    {
      enabled: activeTab === 'sms',
      refetchInterval: 30000, // 30초마다 갱신
    }
  );

  // 알림 대상 업데이트
  const updateTargetMutation = useMutation(
    ({ notificationType, targetType, data }) => 
      api.put(`/notifications/targets/${notificationType}/${targetType}`, data),
    {
      onSuccess: () => {
        toast.success('알림 대상 설정이 저장되었습니다');
        refetchTargets();
      },
      onError: (error) => {
        toast.error('알림 대상 설정 저장에 실패했습니다');
        console.error(error);
      }
    }
  );

  // 템플릿 업데이트
  const updateTemplateMutation = useMutation(
    ({ id, data }) => api.put(`/notifications/templates/${id}`, data),
    {
      onSuccess: () => {
        toast.success('템플릿이 저장되었습니다');
        refetchTemplates();
      },
      onError: (error) => {
        toast.error('템플릿 저장에 실패했습니다');
        console.error(error);
      }
    }
  );

  // API 설정 업데이트
  const updateApiMutation = useMutation(
    ({ provider, data }) => api.put(`/notifications/api-settings/${provider}`, data),
    {
      onSuccess: () => {
        toast.success('API 설정이 저장되었습니다');
        refetchApiSettings();
      },
      onError: (error) => {
        toast.error('API 설정 저장에 실패했습니다');
        console.error(error);
      }
    }
  );

  // 테스트 발송
  const sendTestMutation = useMutation(
    (data) => api.post('/notifications/test', data),
    {
      onSuccess: () => {
        toast.success('테스트 메시지가 발송되었습니다');
      },
      onError: (error) => {
        toast.error('테스트 발송에 실패했습니다');
        console.error(error);
      }
    }
  );

  // 알리고 설정 찾기
  const aligoSetting = apiSettings?.find(s => s.provider === 'aligo') || {};
  const emailSetting = apiSettings?.find(s => s.provider === 'email') || {};
  const fcmSetting = apiSettings?.find(s => s.provider === 'fcm') || {};

  const handleTargetToggle = (notificationType, targetType, method, currentValue) => {
    const data = {};
    if (method === 'sms') data.sms_enabled = !currentValue;
    if (method === 'email') data.email_enabled = !currentValue;
    if (method === 'push') data.push_enabled = !currentValue;
    
    updateTargetMutation.mutate({ notificationType, targetType, data });
  };

  const handleTemplateUpdate = (template) => {
    const subject = document.getElementById(`template-subject-${template.id}`)?.value;
    const content = document.getElementById(`template-content-${template.id}`)?.value;
    
    updateTemplateMutation.mutate({
      id: template.id,
      data: { subject, content }
    });
  };

  const handleSmsUpdate = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateApiMutation.mutate({
      provider: 'aligo',
      data: {
        api_key: formData.get('api_key'),
        user_id: formData.get('user_id'),
        sender: formData.get('sender'),
        is_active: formData.get('is_active') === 'on'
      }
    });
  };

  const handleEmailUpdate = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateApiMutation.mutate({
      provider: 'email',
      data: {
        smtp_host: formData.get('smtp_host'),
        smtp_port: formData.get('smtp_port'),
        smtp_secure: formData.get('smtp_secure') === 'on',
        sender_name: formData.get('sender_name'),
        from: formData.get('email_from'),
        email_from: formData.get('email_from'),
        is_active: formData.get('is_active') === 'on'
      }
    });
  };

  const handleFcmUpdate = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateApiMutation.mutate({
      provider: 'fcm',
      data: {
        project_id: formData.get('project_id'),
        service_account_path: formData.get('service_account_path'),
        is_active: formData.get('is_active') === 'on'
      }
    });
  };

  const handleTestSms = () => {
    if (!testPhone) {
      toast.error('전화번호를 입력해주세요');
      return;
    }
    sendTestMutation.mutate({
      type: 'sms',
      phone: testPhone,
      message: testMessage
    });
  };

  const handleTestEmail = () => {
    if (!testEmail) {
      toast.error('이메일 주소를 입력해주세요');
      return;
    }
    if (!testEmailSubject) {
      toast.error('메일 제목을 입력해주세요');
      return;
    }
    if (!testEmailContent) {
      toast.error('메일 내용을 입력해주세요');
      return;
    }
    sendTestMutation.mutate({
      type: 'email',
      email: testEmail,
      subject: testEmailSubject,
      message: testEmailContent,
      isHtml: testEmailIsHtml
    });
  };

  const tabs = [
    { id: 'targets', name: '발송 대상', icon: UserGroupIcon },
    { id: 'templates', name: '템플릿 관리', icon: DocumentTextIcon },
    { id: 'sms', name: 'SMS 설정', icon: ChatBubbleLeftIcon },
    { id: 'email', name: '이메일 설정', icon: EnvelopeIcon },
    { id: 'fcm', name: 'FCM 설정', icon: DevicePhoneMobileIcon },
    { id: 'warranty', name: '보증 기간 설정', icon: CheckCircleIcon },
  ];

  return (
    <div className="px-2 sm:px-4 py-6 max-w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">알림 설정 관리</h1>
        <p className="mt-1 text-sm text-gray-500">
          알림 발송 대상, 템플릿, API 설정을 관리합니다.
        </p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <tab.icon className={`mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-indigo-500' : 'text-gray-400'}`} />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-8">
        {/* 발송 대상 설정 */}
        {activeTab === 'targets' && (
          <div className="space-y-4">
            {/* 발송 조건 설정 안내 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">📌 자동 발송 조건</h3>
              <ul className="space-y-1 text-xs text-blue-800">
                <li>• <strong>프로젝트 접수:</strong> 새 프로젝트가 접수될 때</li>
                <li>• <strong>프로젝트 배정:</strong> 소장에게 프로젝트가 배정될 때</li>
                <li>• <strong>상태 변경:</strong> 프로젝트 상태가 변경될 때</li>
                <li>• <strong>프로젝트 취소:</strong> 프로젝트 상태가 '취소'로 변경 시</li>
                <li>• <strong>개별 공지:</strong> 관리자가 개별 사용자를 선택하여 공지 등록 시</li>
                <li>• <strong>미처리 알림:</strong> 접수 상태로 설정된 시간 경과 시 (2시간/4시간)</li>
                <li>• <strong>검토 기간 초과:</strong> 검토 상태로 설정된 일수(기본 120일) 경과 시 자동 취소</li>
              </ul>
            </div>
            
            {/* 모바일 뷰 - 아코디언 스타일 */}
            <div className="lg:hidden space-y-3">
              <div className="bg-white rounded-lg shadow-sm p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">알림 발송 대상 설정</h3>
                <p className="text-sm text-gray-500 mt-1">
                  알림 유형을 선택하여 설정하세요.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => {
                      const allExpanded = {};
                      Object.keys(notificationTargets || {}).forEach(key => {
                        allExpanded[key] = true;
                      });
                      setExpandedNotifications(allExpanded);
                    }}
                    className="text-xs px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                  >
                    모두 열기
                  </button>
                  <button
                    onClick={() => setExpandedNotifications({})}
                    className="text-xs px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                  >
                    모두 닫기
                  </button>
                </div>
              </div>
              
              {Object.entries(notificationTargets || {}).map(([notificationType, group]) => (
                <div key={notificationType} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <button
                    onClick={() => setExpandedNotifications(prev => ({
                      ...prev,
                      [notificationType]: !prev[notificationType]
                    }))}
                    className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                  >
                    <span className="font-medium text-gray-900">{group.name}</span>
                    <svg 
                      className={`h-5 w-5 text-gray-500 transform transition-transform ${
                        expandedNotifications[notificationType] ? 'rotate-180' : ''
                      }`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {expandedNotifications[notificationType] && (
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-3">
                        {['admin', 'contractor', 'customer', 'vip_customer'].map((userType) => {
                          const target = group.targets?.find(t => t.target_type === userType);
                          if (!target) return null;
                          
                          const labels = {
                            admin: '관리자',
                            contractor: '소장',
                            customer: '고객',
                            vip_customer: 'VIP'
                          };
                          
                          return (
                            <div key={userType} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <h5 className="text-sm font-medium text-gray-900 mb-3">{labels[userType]}</h5>
                              <div className="space-y-2">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={target.sms_enabled}
                                    onChange={() => handleTargetToggle(notificationType, target.target_type, 'sms', target.sms_enabled)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">SMS</span>
                                </label>
                                
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={target.email_enabled}
                                    onChange={() => handleTargetToggle(notificationType, target.target_type, 'email', target.email_enabled)}
                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">이메일</span>
                                </label>
                                
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={target.push_enabled}
                                    onChange={() => handleTargetToggle(notificationType, target.target_type, 'push', target.push_enabled)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">푸시</span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* PC 뷰 - 테이블 */}
            <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">알림 발송 대상 설정</h3>
                <p className="text-sm text-gray-500 mt-1">
                  각 알림 유형별로 어떤 사용자 그룹에게 발송할지 설정합니다.
                </p>
              </div>

              {/* 테이블 */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        알림 유형
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider" colSpan="3">
                        관리자
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider" colSpan="3">
                        소장
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider" colSpan="3">
                        고객
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider" colSpan="3">
                        VIP
                      </th>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-2"></th>
                      {/* 각 사용자 타입별로 SMS, Email, Push 헤더 */}
                      {[1, 2, 3, 4].map((idx) => (
                        <React.Fragment key={idx}>
                          <th className="px-2 py-2 text-center text-xs text-gray-600">SMS</th>
                          <th className="px-2 py-2 text-center text-xs text-gray-600">메일</th>
                          <th className="px-2 py-2 text-center text-xs text-gray-600">푸시</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(notificationTargets || {}).map(([notificationType, group]) => (
                      <tr key={notificationType} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {group.name}
                        </td>
                        {['admin', 'contractor', 'customer', 'vip_customer'].map((userType) => {
                          const target = group.targets?.find(t => t.target_type === userType);
                          if (!target) return (
                            <React.Fragment key={userType}>
                              <td className="px-2 py-3 text-center text-gray-400">-</td>
                              <td className="px-2 py-3 text-center text-gray-400">-</td>
                              <td className="px-2 py-3 text-center text-gray-400">-</td>
                            </React.Fragment>
                          );
                          
                          return (
                            <React.Fragment key={userType}>
                              <td className="px-2 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={target.sms_enabled}
                                  onChange={() => handleTargetToggle(notificationType, target.target_type, 'sms', target.sms_enabled)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                />
                              </td>
                              <td className="px-2 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={target.email_enabled}
                                  onChange={() => handleTargetToggle(notificationType, target.target_type, 'email', target.email_enabled)}
                                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                                />
                              </td>
                              <td className="px-2 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={target.push_enabled}
                                  onChange={() => handleTargetToggle(notificationType, target.target_type, 'push', target.push_enabled)}
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer"
                                />
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 빠른 설정 버튼들 */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">빠른 설정</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    // 모든 알림 활성화
                    Object.entries(notificationTargets || {}).forEach(([notificationType, group]) => {
                      group.targets?.forEach(target => {
                        if (!target.sms_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { sms_enabled: true } });
                        if (!target.email_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { email_enabled: true } });
                        if (!target.push_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { push_enabled: true } });
                      });
                    });
                  }}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
                >
                  모든 알림 켜기
                </button>
                
                <button
                  onClick={() => {
                    // 모든 알림 비활성화
                    Object.entries(notificationTargets || {}).forEach(([notificationType, group]) => {
                      group.targets?.forEach(target => {
                        if (target.sms_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { sms_enabled: false } });
                        if (target.email_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { email_enabled: false } });
                        if (target.push_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { push_enabled: false } });
                      });
                    });
                  }}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors"
                >
                  모든 알림 끄기
                </button>
                
                <button
                  onClick={() => {
                    // SMS만 모두 켜기
                    Object.entries(notificationTargets || {}).forEach(([notificationType, group]) => {
                      group.targets?.forEach(target => {
                        if (!target.sms_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { sms_enabled: true } });
                      });
                    });
                  }}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                >
                  SMS만 켜기
                </button>
                
                <button
                  onClick={() => {
                    // 이메일만 모두 켜기
                    Object.entries(notificationTargets || {}).forEach(([notificationType, group]) => {
                      group.targets?.forEach(target => {
                        if (!target.email_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { email_enabled: true } });
                      });
                    });
                  }}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
                >
                  이메일만 켜기
                </button>
                
                <button
                  onClick={() => {
                    // 푸시만 모두 켜기
                    Object.entries(notificationTargets || {}).forEach(([notificationType, group]) => {
                      group.targets?.forEach(target => {
                        if (!target.push_enabled) updateTargetMutation.mutate({ notificationType, targetType: target.target_type, data: { push_enabled: true } });
                      });
                    });
                  }}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
                >
                  푸시만 켜기
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 템플릿 관리 */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* 템플릿 필터 버튼 */}
            <div className="bg-white shadow-sm rounded-lg p-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setTemplateFilter('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    templateFilter === 'all'
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  전체
                </button>
                <button
                  onClick={() => setTemplateFilter('SMS')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                    templateFilter === 'SMS'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  <ChatBubbleLeftIcon className="h-4 w-4" />
                  SMS
                </button>
                <button
                  onClick={() => setTemplateFilter('EMAIL')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                    templateFilter === 'EMAIL'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  <EnvelopeIcon className="h-4 w-4" />
                  EMAIL
                </button>
                <button
                  onClick={() => setTemplateFilter('PUSH')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                    templateFilter === 'PUSH'
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                  }`}
                >
                  <DevicePhoneMobileIcon className="h-4 w-4" />
                  PUSH
                </button>
              </div>
            </div>

            {/* 템플릿 목록 */}
            <div className="space-y-8">
              {(() => {
                // 템플릿을 타입별로 그룹화
                const groupedTemplates = templates?.reduce((acc, template) => {
                  const type = template.template_type.toUpperCase();
                  if (!acc[type]) acc[type] = [];
                  acc[type].push(template);
                  return acc;
                }, {}) || {};

                // 필터링 적용
                const filteredGroups = templateFilter === 'all' 
                  ? groupedTemplates 
                  : { [templateFilter]: groupedTemplates[templateFilter] || [] };

                const typeInfo = {
                SMS: { 
                  icon: ChatBubbleLeftIcon, 
                  color: 'blue',
                  bgColor: 'bg-blue-50',
                  borderColor: 'border-blue-200',
                  textColor: 'text-blue-900'
                },
                EMAIL: { 
                  icon: EnvelopeIcon, 
                  color: 'green',
                  bgColor: 'bg-green-50',
                  borderColor: 'border-green-200',
                  textColor: 'text-green-900'
                },
                PUSH: { 
                  icon: DevicePhoneMobileIcon, 
                  color: 'purple',
                  bgColor: 'bg-purple-50',
                  borderColor: 'border-purple-200',
                  textColor: 'text-purple-900'
                }
              };

              const getTemplateKeyLabel = (key) => {
                const labels = {
                  'project_created': '프로젝트 생성',
                  'project_assigned': '프로젝트 배정',
                  'project_status_changed': '상태 변경',
                  'project_completed': '프로젝트 완료',
                  'project_cancelled': '프로젝트 취소',
                  'new_chat_message': '새 메시지',
                  'vip_reward_earned': 'VIP 리워드 적립',
                  'vip_withdrawal_approved': 'VIP 출금 승인',
                  'vip_withdrawal_rejected': 'VIP 출금 거부',
                  'review_received': '⭐ 평가 등록',
                  'individual_notice': '🔒 개별 공지',
                  'general_notice': '📢 전체 공지',
                  'contractor_notice': '👷 소장 공지',
                  'customer_notice': '👤 고객 공지',
                  'vip_notice': '💎 VIP 공지',
                  'auto_cancel_warning': '⚠️ 자동 취소 경고',
                  'auto_cancelled': '❌ 자동 취소',
                  'unprocessed_reminder': '🔔 미처리 알림'
                };
                return labels[key] || key;
              };

                // 템플릿 렌더링
                const templateElements = Object.entries(filteredGroups).map(([type, typeTemplates]) => {
                  const info = typeInfo[type] || typeInfo.SMS;
                  const Icon = info.icon;
                  
                  // 빈 카테고리는 표시하지 않음
                  if (!typeTemplates || typeTemplates.length === 0) return null;

                  return (
                  <div key={type} className="space-y-4">
                    <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${info.bgColor} ${info.borderColor} border`}>
                      <Icon className={`h-5 w-5 ${info.textColor}`} />
                      <h3 className={`text-lg font-semibold ${info.textColor}`}>
                        {type} 템플릿
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {typeTemplates.map((template) => (
                        <div key={template.id} className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                          <div className={`px-6 py-3 ${info.bgColor} border-b ${info.borderColor}`}>
                            <h4 className={`font-medium ${info.textColor}`}>
                              {getTemplateKeyLabel(template.template_key)}
                            </h4>
                            {template.variables && (
                              <p className="mt-1 text-sm text-gray-600">
                                사용 가능한 변수: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{template.variables}</code>
                              </p>
                            )}
                            {type === 'SMS' && (
                              <p className="mt-1 text-xs text-blue-600">
                                <code className="bg-blue-50 px-1 py-0.5 rounded">{'{user_salutation}'}</code> - 사용자별 호칭 (관리자님, 홍길동 소장님, 김철수 고객님)
                              </p>
                            )}
                          </div>

                          <div className="p-6 space-y-4">
                            {template.template_type === 'email' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  이메일 제목
                                </label>
                                <input
                                  type="text"
                                  id={`template-subject-${template.id}`}
                                  defaultValue={template.subject}
                                  className="block w-full px-4 py-3 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                  placeholder="이메일 제목을 입력하세요"
                                />
                              </div>
                            )}

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {type === 'SMS' ? 'SMS 내용' : type === 'EMAIL' ? '이메일 본문' : '푸시 메시지'}
                              </label>
                              <textarea
                                id={`template-content-${template.id}`}
                                rows={type === 'EMAIL' ? 10 : type === 'SMS' ? 6 : 4}
                                defaultValue={template.content}
                                className="block w-full px-4 py-3 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono"
                                placeholder={`${type} 템플릿 내용을 입력하세요.\n변수는 {{변수명}} 형식으로 사용하세요.`}
                              />
                              {type === 'SMS' && (
                                <>
                                  <p className="mt-2 text-xs text-gray-500">
                                    SMS는 90바이트(한글 45자) 초과 시 LMS로 발송됩니다.
                                  </p>
                                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                                    <p className="text-blue-700 font-medium mb-1">사용자별 호칭 예시:</p>
                                    <ul className="text-blue-600 space-y-0.5">
                                      <li>• 관리자: "관리자님"</li>
                                      <li>• 소장: "김철수 소장님"</li>
                                      <li>• 고객/VIP: "홍길동 고객님"</li>
                                    </ul>
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="flex justify-end">
                              <button
                                onClick={() => handleTemplateUpdate(template)}
                                disabled={updateTemplateMutation.isLoading}
                                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white disabled:opacity-50 transition-colors ${
                                  type === 'SMS' 
                                    ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' 
                                    : type === 'EMAIL' 
                                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                                    : 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
                                } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                              >
                                {updateTemplateMutation.isLoading ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    저장 중...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                                    템플릿 저장
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                });

                // 결과 반환
                return (
                  <>
                    {templateElements}
                    
                    {/* 필터링 결과가 없을 때 */}
                    {templateFilter !== 'all' && (!filteredGroups[templateFilter] || filteredGroups[templateFilter].length === 0) && (
                      <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">{templateFilter} 템플릿이 없습니다</h3>
                        <p className="mt-1 text-sm text-gray-500">다른 카테고리를 선택해보세요.</p>
                      </div>
                    )}

                    {/* 템플릿이 전혀 없을 때 */}
                    {(!templates || templates.length === 0) && (
                      <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">템플릿이 없습니다</h3>
                        <p className="mt-1 text-sm text-gray-500">템플릿이 등록되면 여기에 표시됩니다.</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* SMS 설정 */}
        {activeTab === 'sms' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* SMS 잔액 표시 */}
            {smsBalance && smsBalance.success && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 col-span-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-900">SMS 잔액</span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-900">
                      SMS: {smsBalance.balance?.sms_count?.toLocaleString() || 0}건
                    </div>
                    <div className="text-sm text-blue-700">
                      LMS: {smsBalance.balance?.lms_count?.toLocaleString() || 0}건 / 
                      MMS: {smsBalance.balance?.mms_count?.toLocaleString() || 0}건
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSmsUpdate} className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">알리고 SMS API 설정</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">API Key</label>
                  <input
                    type="text"
                    name="api_key"
                    defaultValue={aligoSetting.config?.api_key || ''}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="알리고 API Key"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">User ID</label>
                  <input
                    type="text"
                    name="user_id"
                    defaultValue={aligoSetting.config?.user_id || ''}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="알리고 User ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">발신번호</label>
                  <input
                    type="text"
                    name="sender"
                    defaultValue={aligoSetting.config?.sender || ''}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="010-0000-0000"
                  />
                  <p className="mt-1 text-xs text-gray-500">사전 등록된 발신번호를 입력하세요</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    id="sms_active"
                    defaultChecked={aligoSetting.is_active !== false}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sms_active" className="ml-2 block text-sm text-gray-900">
                    SMS 알림 활성화
                  </label>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={updateApiMutation.isLoading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {updateApiMutation.isLoading ? '저장 중...' : '설정 저장'}
                </button>
              </div>
            </form>

            {/* SMS 테스트 발송 */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">SMS 테스트 발송</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">수신번호</label>
                  <input
                    type="text"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="010-0000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">메시지 내용</label>
                  <textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <button
                  onClick={handleTestSms}
                  disabled={sendTestMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  {sendTestMutation.isLoading ? '발송 중...' : '테스트 발송'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 이메일 설정 */}
        {activeTab === 'email' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* 이메일 SMTP 설정 */}
            <form onSubmit={handleEmailUpdate} className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">이메일 SMTP 설정</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">SMTP 호스트</label>
                    <input
                      type="text"
                      name="smtp_host"
                      defaultValue={emailSetting.config?.smtp_host || 'localhost'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">SMTP 포트</label>
                    <input
                      type="number"
                      name="smtp_port"
                      defaultValue={emailSetting.config?.smtp_port || '25'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="587"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">발신자 이름</label>
                  <input
                    type="text"
                    name="sender_name"
                    defaultValue={emailSetting.config?.sender_name || '건축시공 플랫폼'}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="건축시공 플랫폼"
                  />
                  <p className="mt-1 text-xs text-gray-500">수신자에게 표시될 발신자 이름</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">발신자 이메일</label>
                  <input
                    type="email"
                    name="email_from"
                    defaultValue={emailSetting.config?.from || emailSetting.config?.email_from || 'noreply@2.youngsam.net'}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="noreply@2.youngsam.net"
                  />
                  <p className="mt-1 text-xs text-gray-500">발신자 이메일 주소 (회신 불가능한 주소 권장)</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="smtp_secure"
                    id="smtp_secure"
                    defaultChecked={emailSetting.config?.smtp_secure === true}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="smtp_secure" className="ml-2 block text-sm text-gray-900">
                    SSL/TLS 사용 (포트 465 사용 시 체크)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    id="email_active"
                    defaultChecked={emailSetting.is_active !== false}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="email_active" className="ml-2 block text-sm text-gray-900">
                    이메일 알림 활성화
                  </label>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={updateApiMutation.isLoading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {updateApiMutation.isLoading ? '저장 중...' : '설정 저장'}
                </button>
              </div>
            </form>

            {/* 이메일 테스트 발송 */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">이메일 테스트 발송</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">수신 이메일</label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="test@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">메일 제목</label>
                  <input
                    type="text"
                    value={testEmailSubject}
                    onChange={(e) => setTestEmailSubject(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="테스트 이메일 제목"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    메일 형식
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="emailFormat"
                        checked={!testEmailIsHtml}
                        onChange={() => setTestEmailIsHtml(false)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">텍스트</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="emailFormat"
                        checked={testEmailIsHtml}
                        onChange={() => setTestEmailIsHtml(true)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">HTML</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    메일 내용 {testEmailIsHtml && <span className="text-xs text-gray-500">(HTML 태그 사용 가능)</span>}
                  </label>
                  <textarea
                    value={testEmailContent}
                    onChange={(e) => setTestEmailContent(e.target.value)}
                    rows={testEmailIsHtml ? 10 : 6}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono"
                    placeholder={testEmailIsHtml 
                      ? `<h1>테스트 이메일</h1>\n<p>이것은 <strong>HTML</strong> 형식의 테스트 이메일입니다.</p>\n<ul>\n  <li>목록 항목 1</li>\n  <li>목록 항목 2</li>\n</ul>`
                      : '안녕하세요,\n\n건축시공 플랫폼 테스트 이메일입니다.\n\n감사합니다.'
                    }
                  />
                  {testEmailIsHtml && (
                    <p className="mt-2 text-xs text-gray-500">
                      HTML 태그를 사용하여 서식을 지정할 수 있습니다. (예: &lt;h1&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;a href=""&gt; 등)
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-gray-500">
                    발신: {emailSetting.config?.sender_name || '건축시공 플랫폼'} &lt;{emailSetting.config?.from || 'noreply@2.youngsam.net'}&gt;
                  </div>
                  <button
                    onClick={handleTestEmail}
                    disabled={sendTestMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    {sendTestMutation.isLoading ? '발송 중...' : '테스트 발송'}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <EnvelopeIcon className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">이메일 발송 안내</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>테스트 이메일은 설정된 SMTP 서버를 통해 발송됩니다</li>
                      <li>HTML 형식을 선택하면 다양한 서식을 적용할 수 있습니다</li>
                      <li>발신자는 "{emailSetting.config?.sender_name || '건축시공 플랫폼'} &lt;{emailSetting.config?.from || 'noreply@2.youngsam.net'}&gt;"로 표시됩니다</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 이메일 템플릿 설정 추가 */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <SwatchIcon className="h-5 w-5 mr-2 text-indigo-600" />
                이메일 템플릿 설정
              </h3>
              
              <EmailTemplateSettings 
                selectedTemplate={selectedEmailTemplate}
                setSelectedTemplate={setSelectedEmailTemplate}
                templateSettings={emailTemplateSettings}
                setTemplateSettings={setEmailTemplateSettings}
                emailSetting={emailSetting}
                onPreview={() => setShowEmailPreview(true)}
              />
            </div>
          </div>
        )}

        {/* FCM 설정 */}
        {activeTab === 'fcm' && (
          <div className="space-y-6">
            <form onSubmit={handleFcmUpdate} className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Firebase Cloud Messaging 설정</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">프로젝트 ID</label>
                  <input
                    type="text"
                    name="project_id"
                    defaultValue={fcmSetting.config?.project_id || 'geongi-kr'}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">서비스 계정 파일 경로</label>
                  <input
                    type="text"
                    name="service_account_path"
                    defaultValue={fcmSetting.config?.service_account_path || '/var/www/2.youngsam.net/backend/firebase-service-account.json'}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Firebase 서비스 계정 JSON 파일의 절대 경로</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    id="fcm_active"
                    defaultChecked={fcmSetting.is_active !== false}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="fcm_active" className="ml-2 block text-sm text-gray-900">
                    FCM 푸시 알림 활성화
                  </label>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={updateApiMutation.isLoading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {updateApiMutation.isLoading ? '저장 중...' : '설정 저장'}
                </button>
              </div>
            </form>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <DevicePhoneMobileIcon className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">FCM 테스트</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>FCM 푸시 알림 테스트는 관리자 메뉴의 'FCM 테스트' 페이지에서 진행하세요.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 보증 기간 설정 */}
        {activeTab === 'warranty' && (
          <WarrantyPeriodSettings />
        )}
      </div>
    </div>
  );
};

// 이메일 템플릿 설정 컴포넌트
const EmailTemplateSettings = ({ selectedTemplate, setSelectedTemplate, templateSettings, setTemplateSettings, emailSetting, onPreview }) => {
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('project_status_changed');
  const [selectedUserType, setSelectedUserType] = useState('customer');
  
  // 이메일 템플릿 목록 조회
  useEffect(() => {
    api.get('/notifications/email-templates')
      .then(res => setEmailTemplates(res.data))
      .catch(err => console.error('Failed to load email templates:', err));
  }, []);

  // 현재 설정 로드
  useEffect(() => {
    if (emailSetting?.config) {
      setSelectedTemplate(emailSetting.config.template || 'simple');
      setTemplateSettings(emailSetting.config.template_settings || {});
    }
  }, [emailSetting]);

  const handleTemplateChange = (e) => {
    setSelectedTemplate(e.target.value);
  };

  const handleSettingChange = (key, value) => {
    setTemplateSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveTemplateSettings = async () => {
    try {
      await api.put('/notifications/email-template-settings', {
        template: selectedTemplate,
        template_settings: templateSettings
      });
      toast.success('이메일 템플릿 설정이 저장되었습니다.');
    } catch (error) {
      console.error('Save template settings error:', error);
      toast.error('템플릿 설정 저장에 실패했습니다.');
    }
  };

  const scenarios = {
    project_created: {
      name: '프로젝트 접수',
      data: {
        subject: '프로젝트가 접수되었습니다',
        content: '프로젝트 [강남구 아파트 인테리어]가 성공적으로 접수되었습니다.\n\n담당자가 곧 연락드릴 예정입니다.',
        email_title: '프로젝트 접수 알림',
        project_info: {
          title: '강남구 아파트 인테리어',
          status: '접수',
          status_color: '#6366F1',
          created_date: new Date().toLocaleDateString('ko-KR'),
          construction_type: '인테리어'
        }
      }
    },
    project_status_changed: {
      name: '상태 변경',
      data: {
        subject: '프로젝트 상태가 변경되었습니다',
        content: '프로젝트 [강남구 아파트 인테리어]의 상태가 진행에서 완료로 변경되었습니다.\n\n담당 소장님의 메시지:\n공사가 성공적으로 완료되었습니다. 감사합니다.',
        email_title: '프로젝트 상태 변경 알림',
        project_info: {
          title: '강남구 아파트 인테리어',
          status: '완료',
          status_color: '#10B981',
          previous_status: '진행',
          created_date: '2025-01-15',
          updated_date: new Date().toLocaleDateString('ko-KR'),
          contractor: '김철수',
          construction_type: '인테리어'
        }
      }
    },
    new_chat_message: {
      name: '새 메시지',
      data: {
        subject: '새로운 메시지가 도착했습니다',
        content: '프로젝트 [강남구 아파트 인테리어]에 새로운 메시지가 있습니다.\n\n"안녕하세요. 공사 진행 상황 문의드립니다..."',
        email_title: '새 메시지 알림',
        project_info: {
          title: '강남구 아파트 인테리어',
          status: '진행',
          status_color: '#3B82F6',
          contractor: '김철수'
        },
        additional_message: '메시지를 확인하려면 아래 버튼을 클릭하세요.'
      }
    },
    project_completed: {
      name: '프로젝트 완료',
      data: {
        subject: '프로젝트가 완료되었습니다',
        content: '프로젝트 [강남구 아파트 인테리어]가 성공적으로 완료되었습니다.\n\n공사에 만족하셨다면 리뷰를 남겨주세요.',
        email_title: '프로젝트 완료 알림',
        project_info: {
          title: '강남구 아파트 인테리어',
          status: '완료',
          status_color: '#10B981',
          created_date: '2025-01-15',
          updated_date: new Date().toLocaleDateString('ko-KR'),
          contractor: '김철수',
          construction_type: '인테리어'
        }
      }
    }
  };

  const userTypes = {
    customer: { name: '홍길동', title: ' 고객님' },
    contractor: { name: '김철수', title: ' 소장님' },
    admin: { name: '관리자', title: '님' },
    vip_customer: { name: '이영희', title: ' 고객님' }
  };

  const handlePreview = () => {
    setPreviewLoading(true);
    const scenario = scenarios[selectedScenario];
    const userType = userTypes[selectedUserType];
    
    const previewData = {
      ...scenario.data,
      user_name: userType.name,
      user_title: userType.title,
      url: 'https://2.youngsam.net/projects/123',
      button_text: '자세히 보기',
      ...templateSettings
    };
    
    const previewUrl = `/api/notifications/email-templates/${selectedTemplate}/preview?` + new URLSearchParams(previewData).toString();
    
    window.open(previewUrl, 'EmailPreview', 'width=650,height=800,scrollbars=yes');
    setPreviewLoading(false);
  };

  const currentTemplate = emailTemplates.find(t => t.template_key === selectedTemplate);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">템플릿 선택</label>
        <select
          value={selectedTemplate}
          onChange={handleTemplateChange}
          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          {emailTemplates.map(template => (
            <option key={template.template_key} value={template.template_key}>
              {template.template_name}
            </option>
          ))}
        </select>
      </div>

      {currentTemplate && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">템플릿 설정</h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">회사명</label>
              <input
                type="text"
                value={templateSettings.company_name || ''}
                onChange={(e) => handleSettingChange('company_name', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="건축시공 플랫폼"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">회사 연락처</label>
              <input
                type="text"
                value={templateSettings.company_phone || ''}
                onChange={(e) => handleSettingChange('company_phone', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="1577-1234"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">회사 주소</label>
              <input
                type="text"
                value={templateSettings.company_address || ''}
                onChange={(e) => handleSettingChange('company_address', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="서울시 강남구 테헤란로 123"
              />
            </div>

            {selectedTemplate === 'modern' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">메인 색상</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={templateSettings.primary_color || '#4F46E5'}
                      onChange={(e) => handleSettingChange('primary_color', e.target.value)}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={templateSettings.primary_color || '#4F46E5'}
                      onChange={(e) => handleSettingChange('primary_color', e.target.value)}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">보조 색상</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={templateSettings.secondary_color || '#7C3AED'}
                      onChange={(e) => handleSettingChange('secondary_color', e.target.value)}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={templateSettings.secondary_color || '#7C3AED'}
                      onChange={(e) => handleSettingChange('secondary_color', e.target.value)}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">버튼 색상</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={templateSettings.button_color || '#4F46E5'}
                      onChange={(e) => handleSettingChange('button_color', e.target.value)}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={templateSettings.button_color || '#4F46E5'}
                      onChange={(e) => handleSettingChange('button_color', e.target.value)}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">로고 URL (선택사항)</label>
                  <input
                    type="text"
                    value={templateSettings.logo_url || ''}
                    onChange={(e) => handleSettingChange('logo_url', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </>
            )}

            {selectedTemplate === 'simple' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">강조 색상</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={templateSettings.primary_color || '#2563EB'}
                    onChange={(e) => handleSettingChange('primary_color', e.target.value)}
                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={templateSettings.primary_color || '#2563EB'}
                    onChange={(e) => handleSettingChange('primary_color', e.target.value)}
                    className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 미리보기 시나리오 선택 */}
          <div className="mt-6 border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">미리보기 설정</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">시나리오</label>
                <select
                  value={selectedScenario}
                  onChange={(e) => setSelectedScenario(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  {Object.entries(scenarios).map(([key, scenario]) => (
                    <option key={key} value={key}>{scenario.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">사용자 유형</label>
                <select
                  value={selectedUserType}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="customer">일반 고객</option>
                  <option value="vip_customer">VIP 고객</option>
                  <option value="contractor">소장</option>
                  <option value="admin">관리자</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            <button
              onClick={handleSaveTemplateSettings}
              className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              템플릿 설정 저장
            </button>
            <button
              onClick={handlePreview}
              disabled={previewLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              미리보기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationSettings;